import React, { useEffect, useRef, useState } from 'react'
import './Document.scss'
import UploadOverlay from 'Components/Loader/Overlay/UploadOverlay'
import { useLocation } from 'react-router-dom'
import { DateConverterWord } from 'Helpers'

type IdKey = 'idCard' | 'businessCard' | 'empLetter'

type Props = {
  profile: any
}

const DocumentInfo = ({ profile }: Props) => {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const location = useLocation()

  const fileRefs = {
    idCard: useRef<HTMLInputElement | null>(null),
    businessCard: useRef<HTMLInputElement | null>(null),
    empLetter: useRef<HTMLInputElement | null>(null),
  }
  const [files, setFiles] = useState<Record<IdKey, File | null>>({
    idCard: null,
    businessCard: null,
    empLetter: null,
  })

  const [previews, setPreviews] = useState<Record<IdKey, string | null>>({
    idCard: null,
    businessCard: null,
    empLetter: null,
  })

  const [errors, setErrors] = useState<Record<IdKey, string | null>>({
    idCard: null,
    businessCard: null,
    empLetter: null,
  })

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [previews])

  const handleFile = (key: IdKey, selected?: File) => {
    if (!selected) return

    if (selected.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, [key]: 'File must be less than 5MB' }))
      return
    }

    const localUrl = URL.createObjectURL(selected)

    setFiles((prev) => ({ ...prev, [key]: selected }))
    setPreviews((prev) => ({ ...prev, [key]: localUrl }))
    setErrors((prev) => ({ ...prev, [key]: null }))
  }

  const docMap = {
    idCard: 'ID_CARD',
    businessCard: 'BUSINESS_CARD',
    empLetter: 'EMP_LETTER',
  }

  const handleDrop = (key: IdKey, e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleFile(key, e.dataTransfer.files[0])
  }

  const hideInput = location.pathname.startsWith('/profile')

  const employeeDocuments = profile?.employeeDocuments ?? []
  // console.log('employeeDocuments', employeeDocuments)

  const getLatestDoc = (docs: any, type: any) => {
    return docs
      .filter((d: any) => d.documentName === type)
      .sort(
        (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )[0]
  }

  const latestIdCard = getLatestDoc(employeeDocuments, docMap.idCard)
  const idImageUrl = previews.idCard || latestIdCard?.documentUpload?.url
  const idUpdatedAt = latestIdCard?.updatedAt

  const latestBizCard = getLatestDoc(employeeDocuments, docMap.businessCard)
  const bizImageUrl = previews.businessCard || latestBizCard?.documentUpload?.url
  const bizUpdatedAt = latestBizCard?.updatedAt

  const latestEmpLetter = getLatestDoc(employeeDocuments, docMap.empLetter)
  const empLetterImageUrl = previews.empLetter || latestEmpLetter?.documentUpload?.url
  const empLetterUpdatedAt = latestEmpLetter?.updatedAt

  //   const isImage = file?.type.startsWith('image/')
  //   const isPDF = file?.type === 'application/pdf'

  return (
    <div className='document-info'>
      {/* <form onSubmit={submitEmployeeDeatils}> */}
      <div className='container-fluid m-0 p-0'>
        <div className='row g-4'>
          <div className='col-lg-6'>
            <div className='card'>
              <div className='card-header'>
                <span>ID CARD</span>
              </div>

              <div className='card-bod'>
                <div
                  className='onboard_upload d-flex justify-content-center align-items-center flex-column'
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop('idCard', e)}
                  onClick={() => fileRefs.idCard.current?.click()}
                >
                  <div>
                    <input
                      disabled={hideInput}
                      ref={fileRefs.idCard}
                      type='file'
                      accept='.jpg,.jpeg,.png,.pdf'
                      className='file_input_hidden'
                      onChange={(e) => handleFile('idCard', e.target.files?.[0])}
                    />

                    {idImageUrl ? (
                      <div className='onboard_upload_content'>
                        <div className='onboard_preview'>
                          {files.idCard?.type.startsWith('image/') || latestIdCard ? (
                            <img src={idImageUrl} alt='preview' />
                          ) : null}

                          {files.idCard?.type === 'application/pdf' && !previews.idCard && (
                            <div className='pdf_card'>
                              <a href={idImageUrl} target='_blank' rel='noreferrer'>
                                View Document
                              </a>
                            </div>
                          )}
                        </div>

                        {isUploading && (
                          <div className='progress mt-2'>
                            <div
                              className='progress-bar'
                              role='progressbar'
                              style={{ width: `${progress}%` }}
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              {progress}%
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='before-upload d-flex justify-content-center align-items-center'>
                        <button
                          className='reset-btn-as-link me-2'
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation()
                            fileRefs.idCard.current?.click()
                          }}
                        >
                          {/* <img src={DragAndDropIcon} alt='icon' /> */}
                        </button>
                        {hideInput ? (
                          <p role='button' className='remove-it cursor'>
                            Document will be displayed here once available
                          </p>
                        ) : (
                          <p role='button' className='remove-it cursor'>
                            Drag files here or click to upload
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      {errors.idCard && <div className='text-danger mt-2'>{errors.idCard}</div>}
                    </div>
                  </div>
                </div>
                {idUpdatedAt && (
                  <div className='second-row mb-4 text-muted'>
                    <span>Last updated on</span> <span>{DateConverterWord(idUpdatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='card'>
              <div className='card-header'>
                <span>BUSINESS CARD</span>
              </div>

              <div className='card-bod'>
                <div
                  className='onboard_upload d-flex justify-content-center align-items-center flex-column'
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop('businessCard', e)}
                  onClick={() => fileRefs.businessCard.current?.click()}
                >
                  <div>
                    <input
                      disabled={hideInput}
                      ref={fileRefs.businessCard}
                      type='file'
                      accept='.jpg,.jpeg,.png,.pdf'
                      className='file_input_hidden'
                      onChange={(e) => handleFile('businessCard', e.target.files?.[0])}
                    />

                    {bizImageUrl ? (
                      <div className='onboard_upload_content'>
                        <div className='onboard_preview'>
                          {files.businessCard?.type.startsWith('image/') || latestBizCard ? (
                            <img src={bizImageUrl} alt='preview' />
                          ) : null}

                          {files.businessCard?.type === 'application/pdf' &&
                            !previews.businessCard && (
                              <div className='pdf_card'>
                                <a href={bizImageUrl} target='_blank' rel='noreferrer'>
                                  View Document
                                </a>
                              </div>
                            )}
                        </div>

                        {isUploading && (
                          <div className='progress mt-2'>
                            <div
                              className='progress-bar'
                              role='progressbar'
                              style={{ width: `${progress}%` }}
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              {progress}%
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='before-upload d-flex justify-content-center align-items-center'>
                        <button
                          className='reset-btn-as-link me-2'
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation()
                            fileRefs.businessCard.current?.click()
                          }}
                        >
                          {/* <img src={DragAndDropIcon} alt='icon' /> */}
                        </button>
                        {hideInput ? (
                          <p role='button' className='remove-it cursor'>
                            Document will be displayed here once available
                          </p>
                        ) : (
                          <p role='button' className='remove-it cursor'>
                            Drag files here or click to upload
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    {errors.businessCard && (
                      <div className='text-danger mt-2'>{errors.businessCard}</div>
                    )}
                  </div>
                </div>
                {bizUpdatedAt && (
                  <div className='second-row mb-4 text-muted'>
                    <span>Last updated on</span> <span>{DateConverterWord(bizUpdatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='card'>
              <div className='card-header'>
                <span>EMPLOYMENT LETTER</span>
              </div>

              <div className='card-bod'>
                <div
                  className='onboard_upload d-flex justify-content-center align-items-center flex-column'
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop('empLetter', e)}
                  onClick={() => fileRefs.empLetter.current?.click()}
                >
                  <div>
                    <input
                      disabled={hideInput}
                      ref={fileRefs.empLetter}
                      type='file'
                      accept='.jpg,.jpeg,.png,.pdf'
                      className='file_input_hidden'
                      onChange={(e) => handleFile('empLetter', e.target.files?.[0])}
                    />

                    {empLetterImageUrl ? (
                      <div className='onboard_upload_content d-flex justify-content-center align-items-center'>
                        <div className='onboard_preview'>
                          {files.empLetter?.type.startsWith('image/') || latestEmpLetter ? (
                            <img src={empLetterImageUrl} alt='preview' />
                          ) : null}

                          {files.empLetter?.type === 'application/pdf' && !previews.idCard && (
                            <div className='pdf_card'>
                              <a href={empLetterImageUrl} target='_blank' rel='noreferrer'>
                                View Document
                              </a>
                            </div>
                          )}
                        </div>

                        {isUploading && (
                          <div className='progress mt-2'>
                            <div
                              className='progress-bar'
                              role='progressbar'
                              style={{ width: `${progress}%` }}
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              {progress}%
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='before-upload d-flex justify-content-center align-items-center'>
                        <button
                          className='reset-btn-as-link me-2'
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation()
                            fileRefs.empLetter.current?.click()
                          }}
                        >
                          {/* <img src={DragAndDropIcon} alt='icon' /> */}
                        </button>
                        {hideInput ? (
                          <p role='button' className='remove-it cursor'>
                            Document will be displayed here once available
                          </p>
                        ) : (
                          <p role='button' className='remove-it cursor'>
                            Drag files here or click to upload
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      {errors.empLetter && (
                        <div className='text-danger mt-2'>{errors.empLetter}</div>
                      )}
                    </div>
                  </div>
                </div>
                {empLetterUpdatedAt && (
                  <div className='second-row mb-4 text-muted'>
                    <span>Last updated on</span>{' '}
                    <span>{DateConverterWord(empLetterUpdatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <UploadOverlay isUploading={isUploading} progress={progress} />
    </div>
  )
}

export default DocumentInfo
